import { useSelector } from 'react-redux'
import { CSSTransition } from 'react-transition-group'

import { usePlans } from 'hooks/use-plans'

import { selectCurrentUser } from '../../../../state/selectors'
import { ReactComponent as OrangeTick } from '../../../../styles/assets/checkoutModal/tick-orange.svg'
import { fonts, palette } from '../../../../styles/variables'
import { PROMO_LABEL_VALUE } from '../../../../utils/consts.mjs'
import { isFreeTrialUsed } from '../../../../utils/freeVersionBlock'
import { isLifetimeValid } from '../../../../utils/functions'
import { growthbookFeatures } from '../../../../utils/growthbook'
import { getSubscriptionOptions } from '../../../../utils/payment'
import { translate } from '../../../../utils/translation'
import { withGrowthbookFeature } from '../../../../utils/withGrowthbookFeature'
import { LimitedOffer } from '../../../limitedOffer'
import { Text } from '../../../topography'
import { LoadingSpinner } from '../../../ui/loading/spinner'

import { PlanBadge } from './PlanBadge'
import {
  Item,
  PlanDescriptionContainer,
  PriceContainer,
  PromotionLabel,
  Wrapper,
} from './styled'

const PLANS_CONFIGURATION = {
  [PROMO_LABEL_VALUE]: {
    order: 0,
    theme: 'dark',
    isBlackFriday: true,
    showMonthlyPice: true,
    benefits: [
      {
        value: 'bf_checkout_message',
        type: 'html',
      },
    ],
  },
  monthly: {
    order: 3,
    isTrial: true,
    benefits: ['monthly_access_form_heading', 'no_hidden_fees'],
  },
  yearly: {
    order: 1,
    benefits: [
      {
        value: 'bf_billing_message',
        type: 'html',
      },
    ],
    showMonthlyPice: true,
    limitedOffer: false,
    isBlackFriday: true,
    isTrial: true,
  },
  lifetime: {
    order: -1,
    benefits: ['pay_one_time', 'no_monthly_fees'],
  },
}

const PlanPriceSelector = ({
  interval,
  tier,
  onChange,
  isGrowthbookFeatureOn,
  promotionLabel,
  featureValue,
}) => {
  const { isLoading } = usePlans()

  const user = useSelector(selectCurrentUser())
  const subscriptionOptions = getSubscriptionOptions({
    countryCode: user?.countryCode,
    tier,
    isLifetimeValid: isLifetimeValid(user?.lifetime_offer_until),
    promoFeature: featureValue,
  }).filter((item) => {
    if (isGrowthbookFeatureOn) {
      if (
        user?.subscription?.bfATPPromoClaimed ||
        user?.subscription?.planInterval === 'lifetime' ||
        user?.subscription?.planInterval === 'yearly'
      ) {
        return item.planLabel !== PROMO_LABEL_VALUE
      }
      return true
    }
    if (!isGrowthbookFeatureOn)
      return item.planLabel !== 'yearly' && item.planLabel !== PROMO_LABEL_VALUE
    return true
  })

  if (isLoading) {
    return (
      <Wrapper className="tw-flex !tw-items-center tw-justify-center tw-h-24">
        <LoadingSpinner className="tw-stroke-orange" />
      </Wrapper>
    )
  }

  return (
    <Wrapper>
      {subscriptionOptions
        ?.sort((a, b) => {
          const orderA =
            PLANS_CONFIGURATION[a.planLabel]?.order ?? Number.MAX_SAFE_INTEGER
          const orderB =
            PLANS_CONFIGURATION[b.planLabel]?.order ?? Number.MAX_SAFE_INTEGER
          return orderA - orderB
        })
        ?.map(
          ({
            planLabel,
            discountPrice,
            unitPrice,
            previousePrice,
            monthlyPrice,
            limitedOffer,
          }) => {
            const theme = PLANS_CONFIGURATION?.[planLabel]?.theme || 'light'
            const isBlackFriday =
              PLANS_CONFIGURATION?.[planLabel]?.isBlackFriday
            const selected = interval === planLabel
            let textColor

            if (selected) {
              textColor = palette.orange
            } else {
              textColor = 'inherit'
            }

            return (
              <Item
                key={planLabel}
                theme={theme}
                active={selected}
                onClick={() => onChange(planLabel)}
                isBlackFriday={isBlackFriday}
              >
                {limitedOffer && (
                  <PromotionLabel background={promotionLabel?.background}>
                    <Text
                      fontFamily={fonts.primaryBook}
                      color={promotionLabel?.color || palette.orange}
                      fontSize="12px"
                      fontWeight={500}
                      textTransform="uppercase"
                    >
                      {translate(limitedOffer)}
                    </Text>
                  </PromotionLabel>
                )}
                <CSSTransition
                  in={selected}
                  timeout={300}
                  classNames="fade"
                  unmountOnExit
                >
                  <OrangeTick
                    style={{
                      position: 'absolute',
                      top: '50%',
                      right: '8px',
                      transform: 'translateY(-50%)',
                    }}
                  />
                </CSSTransition>
                <PriceContainer>
                  <Text
                    fontFamily={fonts.primaryBook}
                    fontSize="14px"
                    lineHeight="18px"
                    color={textColor}
                  >
                    {translate(planLabel)}
                  </Text>
                  {previousePrice && (
                    <Text
                      fontSize="18px"
                      lineHeight="18px"
                      color={textColor}
                      style={{ flexShrink: 0 }}
                      textDecoration="line-through"
                    >
                      {previousePrice}
                    </Text>
                  )}
                  <Text
                    fontSize="18px"
                    lineHeight="18px"
                    fontFamily={fonts.primaryMedium}
                    fontWeight={500}
                    color={textColor}
                    style={{ flexShrink: 0 }}
                  >
                    {discountPrice || unitPrice}
                  </Text>
                  {(monthlyPrice &&
                    PLANS_CONFIGURATION?.[planLabel]?.showMonthlyPice && (
                      <Text
                        fontSize="12px"
                        lineHeight="12px"
                        color={palette.orange}
                        style={{ flexShrink: 0 }}
                      >
                        {monthlyPrice}
                        {translate('slash_month')}
                      </Text>
                    )) ||
                    null}
                </PriceContainer>
                <PlanDescriptionContainer theme={theme} selected={selected}>
                  {PLANS_CONFIGURATION?.[planLabel]?.benefits?.map(
                    (benefit, index) => {
                      if (benefit?.type === 'html') {
                        return (
                          <Text
                            key={benefit?.value}
                            fontSize="10px"
                            lineHeight={index === 0 ? '14px' : '10px'}
                            margin="0 5px 0 0"
                            dangerouslySetInnerHTML={{
                              __html: translate(benefit.value),
                            }}
                          />
                        )
                      }
                      return (
                        <Text
                          key={benefit}
                          fontSize="10px"
                          lineHeight={index === 0 ? '14px' : '10px'}
                          color={palette.darkGray50}
                          margin="0 5px 0 0"
                        >
                          {translate(benefit)}.
                        </Text>
                      )
                    },
                  )}
                </PlanDescriptionContainer>
                {PLANS_CONFIGURATION?.[planLabel]?.isTrial
                  ? !isFreeTrialUsed() && (
                      <PlanBadge
                        backgroundColor={
                          PLANS_CONFIGURATION?.[planLabel]?.label
                            ? palette.blue
                            : palette.gray10
                        }
                        color={
                          PLANS_CONFIGURATION?.[planLabel]?.label
                            ? palette.white
                            : palette.gray
                        }
                        text={translate(
                          PLANS_CONFIGURATION?.[planLabel]?.label ||
                            'seven_day_free',
                          7,
                        )}
                      />
                    )
                  : PLANS_CONFIGURATION?.[planLabel]?.save && (
                      <PlanBadge
                        backgroundColor={
                          PLANS_CONFIGURATION?.[planLabel]?.label
                            ? palette.blue
                            : palette.lightGreen
                        }
                        color={
                          PLANS_CONFIGURATION?.[planLabel]?.label
                            ? palette.white
                            : palette.darkGreen
                        }
                        text={translate(
                          PLANS_CONFIGURATION?.[planLabel]?.label ||
                            'save_x_percent',
                          PLANS_CONFIGURATION[planLabel].save,
                        )}
                      />
                    )}
                {PLANS_CONFIGURATION?.[planLabel]?.limitedOffer && (
                  <LimitedOffer />
                )}
              </Item>
            )
          },
        )}
    </Wrapper>
  )
}

export default withGrowthbookFeature(
  PlanPriceSelector,
  growthbookFeatures.PROMO_OFFER,
)
